import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, X, ShieldCheck, MailCheck } from "lucide-react";
import { requestOtp, verifyOtp } from "@/lib/api";
import { TurnstileWidget, captchaEnabled } from "@/components/TurnstileWidget";

// Module-level cache + in-flight map: guarantees exactly ONE /otp/request per
// booking+action even under React StrictMode's double-invoked mount effect.
const recentAutoSends = {};
const inflight = {};

export const OtpDialog = ({ booking, action, onVerified, onClose }) => {
  const [masked, setMasked] = useState("");
  const [code, setCode] = useState("");
  const [seconds, setSeconds] = useState(120);
  const [cooldown, setCooldown] = useState(0);
  const [sending, setSending] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const timerRef = useRef(null);
  const captchaRef = useRef(null);
  const pendingModeRef = useRef(null);

  const startTimer = (start = 120) => {
    setSeconds(start);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setSeconds((s) => {
        if (s <= 1) { clearInterval(timerRef.current); return 0; }
        return s - 1;
      });
    }, 1000);
  };

  const send = async ({ auto = false } = {}, token = "") => {
    const key = `${booking.booking_ref}:${action}`;
    const applyResult = (data, elapsedMs = 0) => {
      setMasked(data.masked_email);
      startTimer(Math.max(1, 120 - Math.floor(elapsedMs / 1000)));
      setCooldown(elapsedMs > 0 ? 0 : 30);
      setSending(false);
    };

    if (auto) {
      const rec = recentAutoSends[key];
      if (rec && Date.now() - rec.ts < 115000) {
        // A code was recently sent — reuse it (close/reopen, remount)
        applyResult({ masked_email: rec.masked }, Date.now() - rec.ts);
        return;
      }
      if (inflight[key]) {
        // StrictMode second invoke: await the SAME request, don't send another
        setSending(true);
        try { applyResult(await inflight[key]); } catch { /* handled by first caller */ }
        return;
      }
    }

    setSending(true);
    const p = requestOtp(booking.booking_ref, action, token);
    if (auto) inflight[key] = p;
    try {
      const data = await p;
      recentAutoSends[key] = { ts: Date.now(), masked: data.masked_email };
      setCode("");
      applyResult(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send verification code");
      setSending(false);
    } finally {
      if (auto) delete inflight[key];
    }
  };

  useEffect(() => {
    if (captchaEnabled) {
      pendingModeRef.current = "auto"; // wait for Turnstile token before sending
    } else {
      send({ auto: true });
    }
    return () => clearInterval(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const expired = seconds <= 0;

  // Turnstile drives the OTP request: send only once a fresh token is available.
  const handleToken = (token) => {
    const mode = pendingModeRef.current;
    if (mode) {
      pendingModeRef.current = null;
      send({ auto: mode === "auto" }, token);
    }
  };

  const handleResend = () => {
    if (captchaEnabled) {
      pendingModeRef.current = "manual";
      setSending(true);
      captchaRef.current?.reset(); // fresh single-use token -> handleToken -> send
    } else {
      send({ auto: false });
    }
  };

  const handleVerify = async () => {
    if (code.trim().length !== 6) { toast.error("Enter the 6-digit code"); return; }
    setVerifying(true);
    try {
      const { verify_token } = await verifyOtp(booking.booking_ref, action, code.trim());
      toast.success("Verified");
      clearInterval(timerRef.current);
      onVerified(verify_token);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid or expired verification code");
    } finally {
      setVerifying(false);
    }
  };

  const mm = String(Math.floor(seconds / 60)).padStart(1, "0");
  const ss = String(seconds % 60).padStart(2, "0");

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="otp-dialog">
      <div className="w-full max-w-sm rounded-xl bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="flex items-center gap-2 font-heading text-lg font-bold text-[#0F172A]">
            <ShieldCheck className="h-5 w-5 text-[#5C2D91]" /> Verify Your Booking
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600" data-testid="otp-close"><X className="h-5 w-5" /></button>
        </div>

        <p className="mb-4 text-sm text-[#64748B]">
          Enter the verification code sent to your registered email address.
        </p>
        <div className="mb-4 flex items-center gap-2 rounded-lg bg-slate-50 px-3 py-2 text-sm font-semibold text-[#0F172A]">
          <MailCheck className="h-4 w-4 text-[#5C2D91]" />
          {sending ? "Sending…" : masked || "—"}
        </div>

        <input
          type="text"
          inputMode="numeric"
          maxLength={6}
          value={code}
          disabled={expired || sending}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
          placeholder="6-digit code"
          className="w-full rounded-lg border border-slate-300 px-4 py-3 text-center text-2xl font-bold tracking-[0.5em] outline-none transition-all focus:border-[#5C2D91] focus:ring-1 focus:ring-[#5C2D91] disabled:bg-slate-50"
          data-testid="otp-input"
        />

        {expired ? (
          <p className="mt-3 text-center text-sm font-semibold text-red-500" data-testid="otp-expired">OTP expired</p>
        ) : (
          <p className="mt-3 text-center text-sm text-[#64748B]">Expires in <span className="font-semibold text-[#5C2D91]">{mm}:{ss}</span></p>
        )}

        {captchaEnabled && (
          <div className="mt-4 flex justify-center" data-testid="otp-captcha">
            <TurnstileWidget ref={captchaRef} onToken={handleToken} />
          </div>
        )}

        <div className="mt-5 space-y-2">
          {expired ? (
            <button onClick={handleResend} disabled={cooldown > 0 || sending} className="flex w-full items-center justify-center gap-2 rounded-lg bg-[#5C2D91] px-4 py-2.5 text-sm font-semibold text-white transition-all hover:bg-[#4A2475] disabled:opacity-60" data-testid="otp-resend">
              {cooldown > 0 ? `Request OTP Again (${cooldown}s)` : "Request OTP Again"}
            </button>
          ) : (
            <button onClick={handleVerify} disabled={verifying || sending} className="flex w-full items-center justify-center gap-2 rounded-lg bg-[#5C2D91] px-4 py-2.5 text-sm font-semibold text-white transition-all hover:bg-[#4A2475] active:scale-[0.98] disabled:opacity-60" data-testid="otp-verify">
              {verifying ? <><Loader2 className="h-4 w-4 animate-spin" /> Verifying…</> : "Verify OTP"}
            </button>
          )}
          <button onClick={onClose} className="w-full rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-semibold text-[#0F172A] transition-all hover:bg-slate-50" data-testid="otp-cancel">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};
